"use client";

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../../lib";

const IMAGE_PARTICLE_HALF_WIDTH_MIN = 10;
const IMAGE_PARTICLE_HALF_WIDTH_RANGE = 22;

/** Uniform random rotation in degrees: `min + random * range` (e.g. -45 + [0,90) → [-45, 45)). */
const PARTICLE_ROTATION_DEG_MIN = -45;
const PARTICLE_ROTATION_DEG_RANGE = 90;

/** Peak opacity each particle fades toward: `min + random * range` in [0, 1]. */
const PARTICLE_TARGET_ALPHA_MIN = 0.1;
const PARTICLE_TARGET_ALPHA_RANGE = 0.3;

export interface ParticlesProps {
	className?: string;
	children?: React.ReactNode;
	quantity?: number;
	staticity?: number;
	ease?: number;
	size?: number;
	refresh?: boolean;
	color?: string;
	vx?: number;
	vy?: number;
	/** When set, draws this image instead of circular dots (same motion / magnetism). */
	particleImageSrc?: string;
	/**
	 * Multiplier for image particle sizing (does not affect dot particles).
	 * Useful when an image asset has extra whitespace and would otherwise appear smaller.
	 */
	particleImageSizeMultiplier?: number;
	/**
	 * Scales peak particle opacity (`targetAlpha`). Use `0.5` for quieter
	 * completion-overlay confetti icons (rocket / trophy / skull).
	 */
	particleAlphaScale?: number;
}

function hexToRgb(hex: string): number[] {
	let normalized = hex.replace("#", "");

	if (normalized.length === 3) {
		normalized = normalized
			.split("")
			.map((char) => char + char)
			.join("");
	}

	const hexInt = Number.parseInt(normalized, 16);
	const red = (hexInt >> 16) & 255;
	const green = (hexInt >> 8) & 255;
	const blue = hexInt & 255;
	return [red, green, blue];
}

export const Particles: React.FC<ParticlesProps> = ({
	className,
	children,
	quantity = 100,
	staticity = 50,
	ease = 50,
	size = 0.4,
	refresh = false,
	color = "#ffffff",
	vx = 0,
	vy = 0,
	particleImageSrc,
	particleImageSizeMultiplier = 1,
	particleAlphaScale = 1,
}) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const canvasContainerRef = useRef<HTMLDivElement>(null);
	const context = useRef<CanvasRenderingContext2D | null>(null);
	const circles = useRef<Circle[]>([]);
	const particleImageRef = useRef<HTMLImageElement | null>(null);
	const [imageLoadNonce, setImageLoadNonce] = useState(0);
	const [particleImageErrored, setParticleImageErrored] = useState(false);
	const mouse = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
	const canvasSize = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
	const animationRef = useRef<number | undefined>(undefined);
	const dpr = typeof window !== "undefined" ? window.devicePixelRatio : 1;

	interface Circle {
		x: number;
		y: number;
		translateX: number;
		translateY: number;
		size: number;
		/** Rotation in radians (fixed per particle). */
		rotation: number;
		alpha: number;
		targetAlpha: number;
		dx: number;
		dy: number;
		magnetism: number;
	}

	useEffect(() => {
		if (!particleImageSrc) {
			particleImageRef.current = null;
			setParticleImageErrored(false);
			return;
		}
		let cancelled = false;
		particleImageRef.current = null;
		setParticleImageErrored(false);
		const img = new Image();
		img.onload = () => {
			if (cancelled) {
				return;
			}
			particleImageRef.current = img;
			setParticleImageErrored(false);
			setImageLoadNonce((n) => n + 1);
		};
		img.onerror = () => {
			if (cancelled) {
				return;
			}
			particleImageRef.current = null;
			setParticleImageErrored(true);
			setImageLoadNonce((n) => n + 1);
		};
		img.src = particleImageSrc;
		return () => {
			cancelled = true;
		};
	}, [particleImageSrc]);

	/** Ref-only pointer → no React re-render on mousemove (was causing severe overlay jank). */
	useEffect(() => {
		const handlePointerMove = (event: MouseEvent) => {
			const canvas = canvasRef.current;
			if (!canvas) {
				return;
			}
			const rect = canvas.getBoundingClientRect();
			const { w, h } = canvasSize.current;
			if (w <= 0 || h <= 0) {
				return;
			}
			const x = event.clientX - rect.left - w / 2;
			const y = event.clientY - rect.top - h / 2;
			const inside = x < w / 2 && x > -w / 2 && y < h / 2 && y > -h / 2;
			if (inside) {
				mouse.current.x = x;
				mouse.current.y = y;
			}
		};
		window.addEventListener("mousemove", handlePointerMove, {
			passive: true,
		});
		return () => {
			window.removeEventListener("mousemove", handlePointerMove);
		};
	}, []);

	useEffect(() => {
		if (canvasRef.current) {
			context.current = canvasRef.current.getContext("2d");
		}
		initCanvas();
		animate();
		window.addEventListener("resize", initCanvas);
		const container = canvasContainerRef.current;
		let resizeRaf = 0;
		const resizeObserver =
			typeof ResizeObserver !== "undefined" && container
				? new ResizeObserver(() => {
						// Coalesce layout thrash (swiper / overlay transitions) so we
						// do not re-seed the particle field every observer tick.
						if (resizeRaf) {
							return;
						}
						resizeRaf = window.requestAnimationFrame(() => {
							resizeRaf = 0;
							initCanvas();
						});
					})
				: null;
		if (resizeObserver && container) {
			resizeObserver.observe(container);
		}

		return () => {
			window.removeEventListener("resize", initCanvas);
			resizeObserver?.disconnect();
			if (resizeRaf) {
				window.cancelAnimationFrame(resizeRaf);
			}
			if (animationRef.current) {
				cancelAnimationFrame(animationRef.current);
			}
		};
	}, [
		color,
		imageLoadNonce,
		particleImageErrored,
		particleImageSrc,
		particleImageSizeMultiplier,
		quantity,
	]);

	useEffect(() => {
		initCanvas();
	}, [refresh]);

	useEffect(() => {
		// Ensure sizing tweaks re-seed image particles without needing to reload the image.
		initCanvas();
	}, [particleImageSizeMultiplier]);

	const initCanvas = () => {
		resizeCanvas();
		drawParticles();
	};

	const resizeCanvas = () => {
		if (
			canvasContainerRef.current &&
			canvasRef.current &&
			context.current
		) {
			circles.current.length = 0;
			canvasSize.current.w = canvasContainerRef.current.offsetWidth;
			canvasSize.current.h = canvasContainerRef.current.offsetHeight;
			canvasRef.current.width = canvasSize.current.w * dpr;
			canvasRef.current.height = canvasSize.current.h * dpr;
			canvasRef.current.style.width = `${canvasSize.current.w}px`;
			canvasRef.current.style.height = `${canvasSize.current.h}px`;
			context.current.setTransform(1, 0, 0, 1, 0, 0);
			context.current.scale(dpr, dpr);
		}
	};

	const circleParams = (seed?: { x: number; y: number }): Circle => {
		const x = seed ? seed.x : Math.random() * canvasSize.current.w;
		const y = seed ? seed.y : Math.random() * canvasSize.current.h;
		const translateX = 0;
		const translateY = 0;
		const useImageSizing =
			Boolean(particleImageSrc) &&
			!particleImageErrored &&
			Boolean(particleImageRef.current);
		const pSize = useImageSizing
			? (IMAGE_PARTICLE_HALF_WIDTH_MIN +
					Math.random() * IMAGE_PARTICLE_HALF_WIDTH_RANGE) *
				Math.max(0.1, particleImageSizeMultiplier)
			: Math.floor(Math.random() * 2) + size;
		const alpha = 0;
		const alphaScale = Math.max(0, Math.min(1, particleAlphaScale));
		const targetAlpha = Number.parseFloat(
			(
				(PARTICLE_TARGET_ALPHA_MIN +
					Math.random() * PARTICLE_TARGET_ALPHA_RANGE) *
				alphaScale
			).toFixed(3),
		);
		const dx = (Math.random() - 0.5) * 0.1;
		const dy = (Math.random() - 0.5) * 0.1;
		const magnetism = 0.1 + Math.random() * 4;
		const rotation =
			((PARTICLE_ROTATION_DEG_MIN +
				Math.random() * PARTICLE_ROTATION_DEG_RANGE) *
				Math.PI) /
			180;
		return {
			x,
			y,
			translateX,
			translateY,
			size: pSize,
			rotation,
			alpha,
			targetAlpha,
			dx,
			dy,
			magnetism,
		};
	};

	const rgb = hexToRgb(color);

	const particleBoundsRadius = (circle: Circle): number => {
		const img = particleImageRef.current;
		if (
			particleImageSrc &&
			!particleImageErrored &&
			img &&
			img.complete &&
			img.naturalWidth > 0
		) {
			const halfHW = circle.size;
			const halfHH =
				halfHW * (img.naturalHeight / Math.max(1, img.naturalWidth));
			return Math.hypot(halfHW, halfHH);
		}
		return circle.size;
	};

	const drawParticle = (circle: Circle, update = false) => {
		if (!context.current) {
			return;
		}
		const {
			x,
			y,
			translateX,
			translateY,
			size: circleSize,
			rotation,
			alpha,
		} = circle;
		const cx = x + translateX;
		const cy = y + translateY;
		const img = particleImageRef.current;

		if (
			particleImageSrc &&
			!particleImageErrored &&
			img &&
			img.complete &&
			img.naturalWidth > 0
		) {
			const aspect = img.naturalHeight / img.naturalWidth;
			const dw = circleSize * 2;
			const dh = dw * aspect;
			context.current.translate(cx, cy);
			context.current.rotate(rotation);
			context.current.globalAlpha = alpha;
			context.current.drawImage(img, -dw / 2, -dh / 2, dw, dh);
			context.current.globalAlpha = 1;
			context.current.setTransform(dpr, 0, 0, dpr, 0, 0);
		} else {
			context.current.translate(cx, cy);
			context.current.rotate(rotation);
			context.current.beginPath();
			context.current.arc(0, 0, circleSize, 0, 2 * Math.PI);
			context.current.fillStyle = `rgba(${rgb.join(", ")}, ${alpha})`;
			context.current.fill();
			context.current.setTransform(dpr, 0, 0, dpr, 0, 0);
		}

		if (!update) {
			circles.current.push(circle);
		}
	};

	const clearContext = () => {
		if (context.current) {
			context.current.clearRect(
				0,
				0,
				canvasSize.current.w,
				canvasSize.current.h,
			);
		}
	};

	const drawParticles = () => {
		clearContext();
		if (
			particleImageSrc &&
			!particleImageRef.current &&
			!particleImageErrored
		) {
			return;
		}
		const particleCount = quantity;
		const { w, h } = canvasSize.current;
		if (w <= 0 || h <= 0 || particleCount <= 0) {
			return;
		}

		// Stratified seeding (grid-jitter): more even coverage than pure random.
		const aspect = w / h;
		const cols = Math.max(1, Math.ceil(Math.sqrt(particleCount * aspect)));
		const rows = Math.max(1, Math.ceil(particleCount / cols));
		const cellW = w / cols;
		const cellH = h / rows;

		for (let i = 0; i < particleCount; i++) {
			const col = i % cols;
			const row = Math.floor(i / cols);
			const seedX = (col + Math.random()) * cellW;
			const seedY = (row + Math.random()) * cellH;
			const circle = circleParams({ x: seedX, y: seedY });
			drawParticle(circle);
		}
	};

	const remapValue = (
		value: number,
		start1: number,
		end1: number,
		start2: number,
		end2: number,
	): number => {
		const remapped =
			((value - start1) * (end2 - start2)) / (end1 - start1) + start2;
		return remapped > 0 ? remapped : 0;
	};

	const animate = () => {
		clearContext();
		circles.current.forEach((circle: Circle, i: number) => {
			const br = particleBoundsRadius(circle);
			const edge = [
				circle.x + circle.translateX - br,
				canvasSize.current.w - circle.x - circle.translateX - br,
				circle.y + circle.translateY - br,
				canvasSize.current.h - circle.y - circle.translateY - br,
			];
			const closestEdge = edge.reduce((a, b) => Math.min(a, b));
			const remapClosestEdge = Number.parseFloat(
				remapValue(closestEdge, 0, 20, 0, 1).toFixed(2),
			);
			if (remapClosestEdge > 1) {
				circle.alpha += 0.02;
				if (circle.alpha > circle.targetAlpha) {
					circle.alpha = circle.targetAlpha;
				}
			} else {
				circle.alpha = circle.targetAlpha * remapClosestEdge;
			}
			circle.x += circle.dx + vx;
			circle.y += circle.dy + vy;
			circle.translateX +=
				(mouse.current.x / (staticity / circle.magnetism) -
					circle.translateX) /
				ease;
			circle.translateY +=
				(mouse.current.y / (staticity / circle.magnetism) -
					circle.translateY) /
				ease;

			drawParticle(circle, true);

			if (
				circle.x < -br ||
				circle.x > canvasSize.current.w + br ||
				circle.y < -br ||
				circle.y > canvasSize.current.h + br
			) {
				circles.current.splice(i, 1);
				const newCircle = circleParams();
				drawParticle(newCircle);
			}
		});
		animationRef.current = window.requestAnimationFrame(animate);
	};

	return (
		<div
			ref={canvasContainerRef}
			className={cn("overflow-hidden bg-transparent", className)}
		>
			<canvas className="absolute inset-0 size-full" ref={canvasRef} />

			{children ? (
				<div className="relative z-10 h-full w-full">{children}</div>
			) : null}
		</div>
	);
};

Particles.displayName = "Particles";
